import { useState } from 'react';
import { IconRepeat, IconTrash } from '@tabler/icons';

import { shopperService } from '#root/services';
import {
  Button,
  Modal,
  ModalBody,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '#root/components/ui';
import styles from './rejectDialog.module.scss';

/** 0 = rechazar sin republicar el pedido, 1 = rechazar y republicarlo */
type RejectState = 0 | 1;

interface RejectDialogProps {
  /** `ida` del acuerdo */
  id?: number | string;
  open?: boolean;
  onClose: () => void;
  /** Recarga los datos del padre después de rechazar */
  handleLoading: () => void;
  createMessage: {
    success: (msg: string) => void;
    error: (msg: string) => void;
  };
}

/**
 * El comprador rechaza un acuerdo que todavía no pagó, con o sin republicar el pedido. Lo abren
 * el ⋮ del acuerdo (`DealCard/Summary`) y el botón del detalle (`UserDealDetail/Actions`).
 */
const RejectDialog = (props: RejectDialogProps) => {
  const { id, open = false, onClose, handleLoading, createMessage } = props;
  const [submitting, setSubmitting] = useState<RejectState | null>(null);

  const handleReject = async (state: RejectState) => {
    if (submitting !== null) return;
    setSubmitting(state);
    const res = await shopperService.dealReject(id, state);
    setSubmitting(null);
    if (res.error) {
      createMessage.error('No se pudo completar la acción');
      return;
    }
    createMessage.success('Acuerdo rechazado');
    handleLoading();
    onClose();
  };

  // Mientras se envía no se cierra: evita dejar la acción a medias
  const handleClose = () => {
    if (submitting === null) onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalHeader icon={<IconTrash />} tone="error">
        <ModalTitle>Rechazar acuerdo</ModalTitle>
        <ModalDescription>
          Como última alternativa, puedes rechazar este acuerdo porque aún no ha sido pagado. Si
          republicas el pedido, otros viajeros podrán ofertar.
        </ModalDescription>
      </ModalHeader>
      <ModalBody>
        <div className={styles.body}>
          <p>
            El viajero invierte su tiempo para hacerte una oferta y concretar un acuerdo: simula
            la compra, revisa las medidas, calcula su inversión.
          </p>
          <p>
            Concretar un acuerdo y rechazarlo es una conducta que perjudica a la comunidad de
            viajeros de Guvery.
          </p>
          <p className={styles.highlight}>
            Si el acuerdo tiene un error, intenta chatear con el viajero para corregirlo.
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose} disabled={submitting !== null}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          icon={<IconTrash />}
          loading={submitting === 0}
          disabled={submitting === 1}
          onClick={() => handleReject(0)}>
          Solo rechazar
        </Button>
        <Button
          icon={<IconRepeat />}
          loading={submitting === 1}
          disabled={submitting === 0}
          onClick={() => handleReject(1)}>
          Rechazar y republicar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default RejectDialog;
